import { useMemo } from "react";

import { useRealTimeTypedQuery } from "components/WebSocket/hooks";
import { typedQueryClient } from "hooks";
import { useCustomQueriesFilters } from "modules/DMSv3/CustomQueries/hooks/useCustomQueriesFilters";
import { setSelectedQuery, useSelectedQuery } from "modules/DMSv3/CustomQueries/hooks/useSelectedQuery";
import { DMSv3CustomQueriesQueryKeys } from "modules/DMSv3/CustomQueries/queryKeys";
import { ApiInstance } from "utils/Api";

import type { WebSocketMessageListener } from "components";
import type { DMSCustomQueryHistory } from "models";

export const useCustomQueryHistoryQuery = () => {
  const { data: filters } = useCustomQueriesFilters();
  const { data: selectedQuery } = useSelectedQuery();

  const listeners = useMemo<WebSocketMessageListener<DMSCustomQueryHistory>[]>(
    () => [
      {
        model: "DMSCustomQueryHistory",
        action: "upsert",
        callback: ({ data: update }) => {
          const history = typedQueryClient.getQueryData(DMSv3CustomQueriesQueryKeys.history);
          if (!history) return;

          const itemIdx = history.items.findIndex(item => item.id === update.id);
          if (itemIdx === -1) return;

          const updatedItem = { ...history.items[itemIdx], ...update };
          typedQueryClient.setQueryData(DMSv3CustomQueriesQueryKeys.history, { ...history, items: history.items.with(itemIdx, updatedItem) });

          if (selectedQuery?.id === updatedItem.id) setSelectedQuery(updatedItem);
        }
      }
    ],
    [selectedQuery?.id]
  );

  const { data, isFetching, refetch } = useRealTimeTypedQuery({
    queryKey: DMSv3CustomQueriesQueryKeys.history,
    listeners,
    queryFn: async ({ endpoint, baseUrl }) => (await ApiInstance.post(endpoint, { dealer_location_id: filters.dealer_location_id, page: filters.page }, baseUrl)).data,
    enabled: filters.dealer_location_id !== null,
    refetchOnDependencies: [filters.dealer_location_id, filters.page]
  });

  return { history: data, isFetchingHistory: isFetching, refetchHistory: refetch };
};
